import { ref } from 'vue'

import { t } from '@fondof-ui/lib/translations'
import { useCustomerStore } from '@global-store/stores/customer'
import { localizeUrl } from '@internal/locale'
import { useUrlSearchParams } from '@vueuse/core'
import { sprykerApiClient } from 'internal/api-client'
import { login } from 'internal/astro/client'
import type { SubmissionContext } from 'vee-validate'

import { type ApiClientJsonErrors, isHttpError } from '@fond-of/api-client'
import { triggerLoginEvent, triggerLoginFailedEvent } from '@fond-of/event'
import {
  type LoginFormOutput,
  type LoginMigrationOptions,
  type MigrationLoginFormOutput,
  type SecurityBlockerErrorMeta,
  useLoginBlocked,
} from '@fond-of/fondof-ui'
import { forgotPassword, hasUpdatedPassword } from '@fond-of/shop-api'

export enum MigrationSteps {
  MigrationLoginForm,
  NewLoginWithPassword,
  LoginForm,
}

const { email: initialEmail } = useUrlSearchParams<{
  email?: string
}>()
const customerStore = useCustomerStore()

const loginAttemptsRemaining = ref<number>(10)
const email = ref<string>()
const accountExists = ref<boolean>(false)
const loginFailed = ref<boolean>(false)

const currentStep = ref<MigrationSteps>(MigrationSteps.MigrationLoginForm)

export const useLoginMigration = ({
  forgotPasswordCallbackUrl = '/account/orders',
  source = 'navigation',
  afterLogin,
}: LoginMigrationOptions = {}) => {
  const onMigration = async (
    attributes: MigrationLoginFormOutput,
    handlers?: SubmissionContext<MigrationLoginFormOutput>
  ) => {
    const updatedPassword = await hasUpdatedPassword(attributes)

    if (handlers && !updatedPassword.accountExists) {
      handlers.setFieldError(
        'email',
        t('validation.auth.migration.accountNotFound')
      )
      accountExists.value = true
      return
    }

    if (updatedPassword.isUpdated) {
      email.value = attributes.email
      currentStep.value = MigrationSteps.LoginForm
      return
    }

    if (!updatedPassword.isUpdated) {
      email.value = attributes.email
      currentStep.value = MigrationSteps.NewLoginWithPassword
      await forgotPassword({
        ...attributes,
        callbackUrl: forgotPasswordCallbackUrl,
        source,
      })
      return
    }
  }

  const { setBlockedDuration, updateBlockedEnd } = useLoginBlocked()

  const onLogin = async (
    values: LoginFormOutput,
    { setErrors }: SubmissionContext<LoginFormOutput>
  ) => {
    try {
      loginFailed.value = false

      const { accessToken } = await login(values)

      localStorage.setItem('TOKEN_STORAGE_KEY', accessToken)

      const customer = await sprykerApiClient.getLoggedInCustomer({
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      })

      if (!customer) {
        return
      }

      customerStore.login(accessToken)
      customerStore.setCustomer(customer)

      triggerLoginEvent({
        customer,
        source,
      })

      afterLogin?.(values, customer)
    } catch (error) {
      triggerLoginFailedEvent({
        error,
        email: values.email,
        source,
      })

      const isLoginRejectedError =
        isHttpError(error) &&
        (error.response.status === 401 || error.response.status === 429)

      loginFailed.value = true

      // set empty values to show the error state on input fields and show message with error alert
      setErrors({
        email: ' ',
        password: ' ',
      })

      if (!isLoginRejectedError) {
        return
      }

      const { errors } =
        (await error.response.json()) as ApiClientJsonErrors<SecurityBlockerErrorMeta>

      // get security blocker meta object from the errors response
      const meta = errors.find((error) => error.meta)?.meta

      if (!meta) {
        return
      }

      loginAttemptsRemaining.value = meta.attemptsRemaining
      setBlockedDuration(values.email, meta.blockedFor)

      if (loginAttemptsRemaining.value !== 0) {
        return
      }

      const end = Date.now() + meta.blockedFor * 1000
      updateBlockedEnd(values.email, end)

      globalThis.location.href = localizeUrl('/login/blocked', {
        email: values.email,
      })
    }
  }

  const resetAccountExists = () => {
    accountExists.value = false
  }

  if (initialEmail) {
    onMigration({ email: initialEmail })
  }

  return {
    email,
    accountExists,
    currentStep,
    forgotPasswordCallbackUrl,
    loginAttemptsRemaining,
    loginFailed,
    onMigration,
    onLogin,
    resetAccountExists,
  }
}
